/*
 * One list + one editor for a client sub-record collection (addresses,
 * employment, immigration statuses, travel events, dependents). The tabs
 * supply the entity-specific pieces (API calls, draft mapping, zod schema);
 * this owns fetching, ordering, the open editor (a react-hook-form instance)
 * and its saving / deleting state.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useForm,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn
} from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

import type { ClientInvalidateTarget } from "../../caseIde/types";
import { useOnClientInvalidate } from "../lib/clientInvalidation";

const DEFAULT_INVALIDATE_ON: readonly ClientInvalidateTarget[] = ["records"];

export type RecordEditor =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; id: string };

export type RecordCrudConfig<T, Draft extends FieldValues, Payload> = {
  clientId: string;
  /** Toast noun, e.g. "Address". */
  noun: string;
  list: (clientId: string) => Promise<T[]>;
  create: (clientId: string, payload: Payload) => Promise<T>;
  update: (clientId: string, id: string, payload: Payload) => Promise<T>;
  remove: (clientId: string, id: string) => Promise<unknown>;
  getId: (record: T) => string;
  toDraft: (record: T) => Draft;
  emptyDraft: () => Draft;
  toPayload: (draft: Draft) => Payload;
  /** Field-level rules; failures show inline and block the save. */
  schema: z.ZodType<Draft, Draft>;
  sort: (a: T, b: T) => number;
  /** Runs after any successful mutation with the refreshed list (side-effect syncs). */
  afterMutate?: (
    records: T[],
    change: "create" | "update" | "delete"
  ) => Promise<void>;
  /**
   * Client-scoped invalidations (from the client manager agent) that refetch
   * this list. Defaults to `records`; the reminders tab passes `reminders`.
   */
  invalidateOn?: readonly ClientInvalidateTarget[];
};

export type RecordCrud<T, Draft extends FieldValues> = {
  records: T[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<T[]>;
  editor: RecordEditor;
  /** The open editor's form (reset to an empty draft when closed). */
  form: UseFormReturn<Draft>;
  isDirty: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  openCreate: (initial?: Partial<Draft>) => void;
  openEdit: (record: T) => void;
  close: () => void;
  /** Validates, then persists. Resolves true when persisted. */
  save: () => Promise<boolean>;
  /** Persists already-validated values (for `FormSheet`'s `onSubmit`). */
  submit: SubmitHandler<Draft>;
  /** Deletes the record open in the editor. */
  deleteCurrent: () => Promise<boolean>;
  /** Deletes any record by id (e.g. from a card menu). */
  deleteRecord: (id: string) => Promise<boolean>;
};

const sameShape = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

export function useRecordCrud<T, Draft extends FieldValues, Payload>(
  config: RecordCrudConfig<T, Draft, Payload>
): RecordCrud<T, Draft> {
  const {
    clientId,
    noun,
    list,
    create,
    update,
    remove,
    getId,
    toDraft,
    emptyDraft,
    toPayload,
    schema,
    sort,
    afterMutate,
    invalidateOn = DEFAULT_INVALIDATE_ON
  } = config;

  const [records, setRecords] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<RecordEditor>({ mode: "closed" });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const requestRef = useRef(0);

  const form = useForm<Draft>({
    resolver: zodResolver(schema),
    defaultValues: emptyDraft() as never,
    mode: "onSubmit",
    reValidateMode: "onChange"
  });

  const refresh = useCallback(async (): Promise<T[]> => {
    requestRef.current += 1;
    const seq = requestRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const next = await list(clientId);
      const safe = Array.isArray(next) ? next : [];
      if (seq === requestRef.current) setRecords(safe);
      return safe;
    } catch (e) {
      if (seq === requestRef.current) {
        setRecords([]);
        setError(
          e instanceof Error
            ? e.message
            : `Failed to load ${noun.toLowerCase()} records.`
        );
      }
      return [];
    } finally {
      if (seq === requestRef.current) setIsLoading(false);
    }
  }, [clientId, list, noun]);

  const close = useCallback(() => {
    setEditor({ mode: "closed" });
    form.reset(emptyDraft());
  }, [emptyDraft, form]);

  useEffect(() => {
    setEditor({ mode: "closed" });
    refresh().catch(() => undefined);
  }, [refresh]);

  // An agent changed this collection: refetch, leaving any open editor alone
  // (the saved-snapshot sync below reconciles it).
  useOnClientInvalidate(clientId, invalidateOn, () => {
    refresh().catch(() => undefined);
  });

  const sorted = useMemo(() => [...records].sort(sort), [records, sort]);

  const openCreate = useCallback(
    (initial?: Partial<Draft>) => {
      form.reset({ ...emptyDraft(), ...(initial ?? {}) });
      setEditor({ mode: "create" });
    },
    [emptyDraft, form]
  );

  const openEdit = useCallback(
    (record: T) => {
      form.reset(toDraft(record));
      setEditor({ mode: "edit", id: getId(record) });
    },
    [form, getId, toDraft]
  );

  // Keep an open editor's saved snapshot aligned with refreshed data.
  useEffect(() => {
    if (editor.mode !== "edit") return;
    const latest = records.find((r) => getId(r) === editor.id);
    if (!latest) return;
    const d = toDraft(latest);
    if (sameShape(d, form.formState.defaultValues)) return;
    form.reset(d, { keepDirtyValues: true });
    // Intentionally keyed on `records` only: re-sync the saved snapshot when
    // the server list changes, not on every editor keystroke.
  }, [records]);

  const persist = useCallback(
    async (values: Draft): Promise<boolean> => {
      if (editor.mode === "closed" || isSaving) return false;
      setIsSaving(true);
      try {
        const payload = toPayload(values);
        const change = editor.mode === "create" ? "create" : "update";
        if (editor.mode === "create") {
          await create(clientId, payload);
          toast.success(`${noun} added`);
        } else {
          await update(clientId, editor.id, payload);
          toast.success("Changes saved");
        }
        const refreshed = await refresh();
        if (afterMutate) await afterMutate(refreshed, change);
        close();
        return true;
      } catch (e) {
        toast.error("Couldn't save changes", {
          description: e instanceof Error ? e.message : undefined
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [
      afterMutate,
      clientId,
      close,
      create,
      editor,
      isSaving,
      noun,
      refresh,
      toPayload,
      update
    ]
  );

  const submit = useCallback<SubmitHandler<Draft>>(
    async (values) => {
      await persist(values);
    },
    [persist]
  );

  const save = useCallback(async (): Promise<boolean> => {
    if (editor.mode === "closed" || isSaving) return false;
    let ok = false;
    await form.handleSubmit(async (values) => {
      ok = await persist(values);
    })();
    return ok;
  }, [editor.mode, form, isSaving, persist]);

  const deleteRecord = useCallback(
    async (id: string): Promise<boolean> => {
      if (isDeleting) return false;
      setIsDeleting(true);
      try {
        await remove(clientId, id);
        toast.success(`${noun} deleted`);
        const refreshed = await refresh();
        if (afterMutate) await afterMutate(refreshed, "delete");
        if (editor.mode === "edit" && editor.id === id) close();
        return true;
      } catch (e) {
        toast.error(`Couldn't delete ${noun.toLowerCase()}`, {
          description: e instanceof Error ? e.message : undefined
        });
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [afterMutate, clientId, close, editor, isDeleting, noun, refresh, remove]
  );

  const deleteCurrent = useCallback(async (): Promise<boolean> => {
    if (editor.mode !== "edit") return false;
    return deleteRecord(editor.id);
  }, [deleteRecord, editor]);

  const isDirty =
    editor.mode === "create"
      ? true
      : editor.mode === "edit"
      ? form.formState.isDirty
      : false;

  return {
    records: sorted,
    isLoading,
    error,
    refresh,
    editor,
    form,
    isDirty,
    isSaving,
    isDeleting,
    openCreate,
    openEdit,
    close,
    save,
    submit,
    deleteCurrent,
    deleteRecord
  };
}
